import React, { useState, useEffect, useCallback } from 'react';
import { cn } from '../../lib/utils';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import {
  Shield,
  Smartphone,
  Globe,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Search,
  RefreshCw,
  Eye,
  Trash2,
  Activity,
  Users,
  Clock,
  MapPin,
  UserPlus,
  Wifi,
  TrendingUp,
  Crown,
} from 'lucide-react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import {
  securityApi,
  Session,
  AuditLog,
  SecurityStats,
  ActivityResponse,
} from '../../api/security';
import { Pagination } from '../../components/shared';
import { toast } from 'sonner';

interface SecurityPageProps {
  isDark: boolean;
}

const ITEMS_PER_PAGE = 20;

const eventTypeColors: Record<string, string> = {
  LOGIN_SUCCESS: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300',
  LOGIN_FAILED: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300',
  LOGOUT: 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300',
  SESSION_CREATED: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300',
  SESSION_REVOKED: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-300',
  DEVICE_LIMIT_EXCEEDED: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300',
  GEO_ANOMALY: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300',
  ACCOUNT_SHARING_SUSPECTED: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300',
  RAPID_LOCATION_CHANGE: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300',
};

const severityColors: Record<string, string> = {
  INFO: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300',
  WARNING: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300',
  CRITICAL: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300',
};

export default function SecurityPage({ isDark }: SecurityPageProps) {
  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState<SecurityStats | null>(null);
  const [activity, setActivity] = useState<ActivityResponse | null>(null);
  const [activityLoading, setActivityLoading] = useState(false);
  const [sessions, setSessions] = useState<Session[]>([]);
  const [auditLogs, setAuditLogs] = useState<AuditLog[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  
  // Sessions state
  const [sessionsPage, setSessionsPage] = useState(1);
  const [sessionsTotal, setSessionsTotal] = useState(0);
  const [sessionsSearch, setSessionsSearch] = useState('');
  const [sessionsActiveFilter, setSessionsActiveFilter] = useState<string>('all');
  const [sessionsPlatformFilter, setSessionsPlatformFilter] = useState<string>('all');
  
  // Audit logs state
  const [logsPage, setLogsPage] = useState(1);
  const [logsTotal, setLogsTotal] = useState(0);
  const [logsSearch, setLogsSearch] = useState('');
  const [logsSeverityFilter, setLogsSeverityFilter] = useState<string>('all');
  const [logsEventTypeFilter, setLogsEventTypeFilter] = useState<string>('all');
  
  // Dialogs
  const [selectedSession, setSelectedSession] = useState<Session | null>(null);
  const [sessionToRevoke, setSessionToRevoke] = useState<Session | null>(null);

  const loadStats = useCallback(async () => {
    try {
      const data = await securityApi.getStats();
      setStats(data);
    } catch (error) {
      console.error('Failed to load stats:', error);
      toast.error('Failed to load security stats');
    }
  }, []);

  const loadSessions = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await securityApi.getSessions({
        page: sessionsPage,
        limit: ITEMS_PER_PAGE,
        search: sessionsSearch || undefined,
        isActive: sessionsActiveFilter !== 'all' ? sessionsActiveFilter : undefined,
        platform: sessionsPlatformFilter !== 'all' ? sessionsPlatformFilter : undefined,
      });
      setSessions(response.data);
      setSessionsTotal(response.total);
    } catch (error) {
      console.error('Failed to load sessions:', error);
      toast.error('Failed to load sessions');
    } finally {
      setIsLoading(false);
    }
  }, [sessionsPage, sessionsSearch, sessionsActiveFilter, sessionsPlatformFilter]);

  const loadActivity = useCallback(async () => {
    setActivityLoading(true);
    try {
      const data = await securityApi.getActivity();
      setActivity(data);
    } catch (error) {
      console.error('Failed to load activity:', error);
      toast.error('Failed to load activity');
    } finally {
      setActivityLoading(false);
    }
  }, []);

  const loadAuditLogs = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await securityApi.getAuditLogs({
        page: logsPage,
        limit: ITEMS_PER_PAGE,
        search: logsSearch || undefined,
        severity: logsSeverityFilter !== 'all' ? logsSeverityFilter : undefined,
        eventType: logsEventTypeFilter !== 'all' ? logsEventTypeFilter : undefined,
      });
      setAuditLogs(response.data);
      setLogsTotal(response.total);
    } catch (error) {
      console.error('Failed to load audit logs:', error);
      toast.error('Failed to load audit logs');
    } finally {
      setIsLoading(false);
    }
  }, [logsPage, logsSearch, logsSeverityFilter, logsEventTypeFilter]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  useEffect(() => {
    if (activeTab === 'sessions') {
      loadSessions();
    }
  }, [activeTab, loadSessions]);

  useEffect(() => {
    if (activeTab === 'activity') {
      loadActivity();
    }
  }, [activeTab, loadActivity]);

  useEffect(() => {
    if (activeTab === 'audit-logs') {
      loadAuditLogs();
    }
  }, [activeTab, loadAuditLogs]);

  const handleRevokeSession = async () => {
    if (!sessionToRevoke) return;
    
    try {
      await securityApi.revokeSession(sessionToRevoke.id);
      toast.success('Session revoked successfully');
      setSessionToRevoke(null);
      loadSessions();
      loadStats();
    } catch (error) {
      console.error('Failed to revoke session:', error);
      toast.error('Failed to revoke session');
    }
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatRelativeTime = (date: string) => {
    const now = new Date();
    const then = new Date(date);
    const diffMs = now.getTime() - then.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className={cn(
            'text-2xl lg:text-3xl font-bold tracking-tight',
            isDark ? 'text-white' : 'text-[#0F172A]'
          )}>Security</h1>
          <p className={cn(
            'text-sm mt-1',
            isDark ? 'text-slate-400' : 'text-slate-500'
          )}>Monitor sessions, audit logs and security events</p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            loadStats();
            if (activeTab === 'sessions') loadSessions();
            if (activeTab === 'activity') loadActivity();
            if (activeTab === 'audit-logs') loadAuditLogs();
          }}
          className={cn(isDark && 'border-slate-700 text-slate-300 hover:bg-slate-800')}
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className={cn(
          'grid w-full grid-cols-4',
          isDark && 'bg-slate-800'
        )}>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="activity">Activity</TabsTrigger>
          <TabsTrigger value="sessions">Sessions</TabsTrigger>
          <TabsTrigger value="audit-logs">Audit Logs</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          {/* Top row: live + reach */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <KpiCard
              isDark={isDark}
              icon={<Wifi className="w-6 h-6 text-emerald-600" />}
              tone="emerald"
              label="Online Now"
              value={stats?.onlineNow ?? 0}
              hint="active in last 5 min"
              live={(stats?.onlineNow ?? 0) > 0}
            />
            <KpiCard
              isDark={isDark}
              icon={<Activity className="w-6 h-6 text-blue-600" />}
              tone="blue"
              label="DAU (today)"
              value={stats?.dau ?? 0}
              hint="unique users"
            />
            <KpiCard
              isDark={isDark}
              icon={<TrendingUp className="w-6 h-6 text-purple-600" />}
              tone="purple"
              label="WAU (7d)"
              value={stats?.wau ?? 0}
              hint="weekly reach"
            />
            <KpiCard
              isDark={isDark}
              icon={<Users className="w-6 h-6 text-indigo-600" />}
              tone="indigo"
              label="MAU (30d)"
              value={stats?.mau ?? 0}
              hint="monthly reach"
            />
          </div>

          {/* Second row: registrations + sessions */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <KpiCard
              isDark={isDark}
              icon={<Users className="w-6 h-6 text-slate-600" />}
              tone="slate"
              label="Total Users"
              value={stats?.totalUsers ?? 0}
            />
            <KpiCard
              isDark={isDark}
              icon={<UserPlus className="w-6 h-6 text-green-600" />}
              tone="green"
              label="New Today"
              value={stats?.newUsersToday ?? 0}
              hint={`${stats?.newUsersThisWeek ?? 0} this week`}
            />
            <KpiCard
              isDark={isDark}
              icon={<Smartphone className="w-6 h-6 text-cyan-600" />}
              tone="cyan"
              label="Active Sessions"
              value={stats?.activeSessions ?? 0}
              hint={`${stats?.uniqueActiveUsers ?? 0} unique users`}
            />
            <KpiCard
              isDark={isDark}
              icon={<AlertTriangle className="w-6 h-6 text-amber-600" />}
              tone="amber"
              label="Suspicious (7d)"
              value={stats?.suspiciousActivity7d ?? 0}
              hint={`${stats?.criticalEvents24h ?? 0} critical 24h`}
            />
          </div>

          {/* Additional Stats */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Login Stats */}
            <Card className={cn(
              'p-6 rounded-2xl border-0',
              isDark ? 'bg-[#1E293B]' : 'bg-white'
            )}>
              <h3 className={cn(
                'text-lg font-semibold mb-4',
                isDark ? 'text-white' : 'text-slate-900'
              )}>Login Activity (24h)</h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-green-500" />
                    <span className={isDark ? 'text-slate-300' : 'text-slate-600'}>Successful Logins</span>
                  </div>
                  <span className={cn('font-semibold', isDark ? 'text-white' : 'text-slate-900')}>
                    {stats?.successfulLogins24h || 0}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <XCircle className="w-5 h-5 text-red-500" />
                    <span className={isDark ? 'text-slate-300' : 'text-slate-600'}>Failed Logins</span>
                  </div>
                  <span className={cn('font-semibold', isDark ? 'text-white' : 'text-slate-900')}>
                    {stats?.failedLogins24h || 0}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Trash2 className="w-5 h-5 text-orange-500" />
                    <span className={isDark ? 'text-slate-300' : 'text-slate-600'}>Revoked Sessions</span>
                  </div>
                  <span className={cn('font-semibold', isDark ? 'text-white' : 'text-slate-900')}>
                    {stats?.revokedSessions24h || 0}
                  </span>
                </div>
              </div>
            </Card>

            {/* Top Countries */}
            <Card className={cn(
              'p-6 rounded-2xl border-0',
              isDark ? 'bg-[#1E293B]' : 'bg-white'
            )}>
              <h3 className={cn(
                'text-lg font-semibold mb-4',
                isDark ? 'text-white' : 'text-slate-900'
              )}>Top Countries</h3>
              <div className="space-y-3">
                {stats?.topCountries?.slice(0, 5).map((item, index) => (
                  <div key={item.country || index} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Globe className="w-4 h-4 text-slate-400" />
                      <span className={isDark ? 'text-slate-300' : 'text-slate-600'}>
                        {item.country || 'Unknown'}
                      </span>
                    </div>
                    <span className={cn('font-semibold', isDark ? 'text-white' : 'text-slate-900')}>
                      {item.count}
                    </span>
                  </div>
                ))}
                {(!stats?.topCountries || stats.topCountries.length === 0) && (
                  <p className={cn('text-sm', isDark ? 'text-slate-500' : 'text-slate-400')}>
                    No data available
                  </p>
                )}
              </div>
            </Card>
          </div>
        </TabsContent>

        {/* Activity Tab */}
        <TabsContent value="activity" className="space-y-6">
          {/* DAU/WAU/MAU big numbers */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <BigStatCard
              isDark={isDark}
              label="Daily Active"
              value={stats?.dau ?? 0}
              accent="blue"
              icon={<Activity className="w-5 h-5" />}
            />
            <BigStatCard
              isDark={isDark}
              label="Weekly Active"
              value={stats?.wau ?? 0}
              accent="purple"
              icon={<TrendingUp className="w-5 h-5" />}
            />
            <BigStatCard
              isDark={isDark}
              label="Monthly Active"
              value={stats?.mau ?? 0}
              accent="indigo"
              icon={<Users className="w-5 h-5" />}
            />
          </div>

          {/* Daily activity chart (30 days) */}
          <Card
            className={cn('p-6 rounded-2xl border-0', isDark ? 'bg-[#1E293B]' : 'bg-white')}
          >
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3
                  className={cn(
                    'text-lg font-semibold',
                    isDark ? 'text-white' : 'text-slate-900',
                  )}
                >
                  Daily Activity — Last 30 Days
                </h3>
                <p
                  className={cn(
                    'text-sm',
                    isDark ? 'text-slate-400' : 'text-slate-500',
                  )}
                >
                  Active users vs new registrations per day
                </p>
              </div>
            </div>
            {activityLoading && !activity ? (
              <div className="h-72 flex items-center justify-center">
                <RefreshCw className="w-6 h-6 animate-spin text-slate-400" />
              </div>
            ) : (
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart
                    data={(activity?.daily ?? []).map((d) => ({
                      ...d,
                      label: new Date(d.date).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                      }),
                    }))}
                  >
                    <defs>
                      <linearGradient id="activeGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#0EA5A4" stopOpacity={0.4} />
                        <stop offset="95%" stopColor="#0EA5A4" stopOpacity={0.05} />
                      </linearGradient>
                      <linearGradient id="newUsersGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#8B5CF6" stopOpacity={0.4} />
                        <stop offset="95%" stopColor="#8B5CF6" stopOpacity={0.05} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke={isDark ? '#334155' : '#E2E8F0'}
                    />
                    <XAxis
                      dataKey="label"
                      tick={{ fill: isDark ? '#94A3B8' : '#64748B', fontSize: 11 }}
                      axisLine={{ stroke: isDark ? '#334155' : '#E2E8F0' }}
                      interval={3}
                    />
                    <YAxis
                      tick={{ fill: isDark ? '#94A3B8' : '#64748B', fontSize: 11 }}
                      axisLine={{ stroke: isDark ? '#334155' : '#E2E8F0' }}
                      allowDecimals={false}
                    />
                    <RechartsTooltip
                      contentStyle={{
                        background: isDark ? '#0F172A' : '#fff',
                        border: `1px solid ${isDark ? '#334155' : '#E2E8F0'}`,
                        borderRadius: 12,
                        color: isDark ? '#F1F5F9' : '#0F172A',
                      }}
                    />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Area
                      type="monotone"
                      dataKey="activeUsers"
                      name="Active Users"
                      stroke="#0EA5A4"
                      strokeWidth={2}
                      fill="url(#activeGradient)"
                    />
                    <Area
                      type="monotone"
                      dataKey="newUsers"
                      name="New Users"
                      stroke="#8B5CF6"
                      strokeWidth={2}
                      fill="url(#newUsersGradient)"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            )}
          </Card>

          {/* Two columns: Online Now + Top Active */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Online Now */}
            <Card
              className={cn(
                'rounded-2xl border-0',
                isDark ? 'bg-[#1E293B]' : 'bg-white',
              )}
            >
              <div
                className={cn(
                  'p-5 border-b flex items-center justify-between',
                  isDark ? 'border-slate-700' : 'border-slate-200',
                )}
              >
                <div className="flex items-center gap-3">
                  <span className="relative flex h-3 w-3">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                    <span className="relative inline-flex rounded-full h-3 w-3 bg-emerald-500" />
                  </span>
                  <h3
                    className={cn(
                      'text-lg font-semibold',
                      isDark ? 'text-white' : 'text-slate-900',
                    )}
                  >
                    Online Now
                  </h3>
                </div>
                <Badge className="bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300">
                  {activity?.onlineNow.length ?? 0} users
                </Badge>
              </div>
              <div className="max-h-[400px] overflow-y-auto">
                {activityLoading && !activity ? (
                  <div className="p-8 text-center">
                    <RefreshCw className="w-6 h-6 animate-spin mx-auto text-slate-400" />
                  </div>
                ) : (activity?.onlineNow.length ?? 0) === 0 ? (
                  <p
                    className={cn(
                      'p-8 text-sm text-center',
                      isDark ? 'text-slate-500' : 'text-slate-400',
                    )}
                  >
                    No users active in the last 5 minutes
                  </p>
                ) : (
                  <ul
                    className={cn(
                      'divide-y',
                      isDark ? 'divide-slate-700' : 'divide-slate-100',
                    )}
                  >
                    {activity?.onlineNow.map((u) => (
                      <li
                        key={u.id}
                        className="px-5 py-3 flex items-center justify-between gap-3"
                      >
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <p
                              className={cn(
                                'font-medium truncate',
                                isDark ? 'text-white' : 'text-slate-900',
                              )}
                            >
                              {u.username || u.id.slice(0, 8)}
                            </p>
                            {u.isSubscribed && (
                              <Crown className="w-3.5 h-3.5 text-amber-500 flex-shrink-0" />
                            )}
                            {u.isAnonymous && (
                              <Badge
                                variant="outline"
                                className="text-[10px] px-1.5 py-0 h-4"
                              >
                                anon
                              </Badge>
                            )}
                          </div>
                          <p
                            className={cn(
                              'text-xs truncate',
                              isDark ? 'text-slate-400' : 'text-slate-500',
                            )}
                          >
                            {u.email || '—'}
                          </p>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <p
                            className={cn(
                              'text-xs',
                              isDark ? 'text-slate-300' : 'text-slate-700',
                            )}
                          >
                            {u.platform}
                          </p>
                          <p
                            className={cn(
                              'text-xs',
                              isDark ? 'text-slate-500' : 'text-slate-400',
                            )}
                          >
                            {formatRelativeTime(u.lastActive)}
                          </p>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </Card>

            {/* Top Active Users 7d */}
            <Card
              className={cn(
                'rounded-2xl border-0',
                isDark ? 'bg-[#1E293B]' : 'bg-white',
              )}
            >
              <div
                className={cn(
                  'p-5 border-b',
                  isDark ? 'border-slate-700' : 'border-slate-200',
                )}
              >
                <h3
                  className={cn(
                    'text-lg font-semibold',
                    isDark ? 'text-white' : 'text-slate-900',
                  )}
                >
                  Top Active Users (7d)
                </h3>
                <p
                  className={cn(
                    'text-sm',
                    isDark ? 'text-slate-400' : 'text-slate-500',
                  )}
                >
                  Most sessions in the last week
                </p>
              </div>
              <div className="max-h-[400px] overflow-y-auto">
                {activityLoading && !activity ? (
                  <div className="p-8 text-center">
                    <RefreshCw className="w-6 h-6 animate-spin mx-auto text-slate-400" />
                  </div>
                ) : (activity?.topUsers.length ?? 0) === 0 ? (
                  <p
                    className={cn(
                      'p-8 text-sm text-center',
                      isDark ? 'text-slate-500' : 'text-slate-400',
                    )}
                  >
                    No activity in the last 7 days
                  </p>
                ) : (
                  <ul
                    className={cn(
                      'divide-y',
                      isDark ? 'divide-slate-700' : 'divide-slate-100',
                    )}
                  >
                    {activity?.topUsers.map((u, idx) => (
                      <li
                        key={u.id}
                        className="px-5 py-3 flex items-center justify-between gap-3"
                      >
                        <div className="flex items-center gap-3 min-w-0 flex-1">
                          <div
                            className={cn(
                              'w-7 h-7 rounded-lg flex items-center justify-center text-xs font-semibold flex-shrink-0',
                              idx === 0
                                ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300'
                                : idx === 1
                                ? 'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-200'
                                : idx === 2
                                ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300'
                                : isDark
                                ? 'bg-slate-800 text-slate-400'
                                : 'bg-slate-50 text-slate-500',
                            )}
                          >
                            {idx + 1}
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2">
                              <p
                                className={cn(
                                  'font-medium truncate',
                                  isDark ? 'text-white' : 'text-slate-900',
                                )}
                              >
                                {u.username || u.id.slice(0, 8)}
                              </p>
                              {u.isSubscribed && (
                                <Crown className="w-3.5 h-3.5 text-amber-500 flex-shrink-0" />
                              )}
                              {u.isAnonymous && (
                                <Badge
                                  variant="outline"
                                  className="text-[10px] px-1.5 py-0 h-4"
                                >
                                  anon
                                </Badge>
                              )}
                            </div>
                            <p
                              className={cn(
                                'text-xs truncate',
                                isDark ? 'text-slate-400' : 'text-slate-500',
                              )}
                            >
                              {u.email || '—'}
                            </p>
                          </div>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <p
                            className={cn(
                              'text-sm font-semibold',
                              isDark ? 'text-white' : 'text-slate-900',
                            )}
                          >
                            {u.sessionCount}
                          </p>
                          <p
                            className={cn(
                              'text-xs',
                              isDark ? 'text-slate-500' : 'text-slate-400',
                            )}
                          >
                            sessions
                          </p>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </Card>
          </div>

          {/* Platform breakdown for today */}
          <Card
            className={cn('p-6 rounded-2xl border-0', isDark ? 'bg-[#1E293B]' : 'bg-white')}
          >
            <h3
              className={cn(
                'text-lg font-semibold mb-4',
                isDark ? 'text-white' : 'text-slate-900',
              )}
            >
              Today by Platform
            </h3>
            <div className="grid grid-cols-3 gap-4">
              {(['ios', 'android', 'web'] as const).map((p) => {
                const count = activity?.byPlatform?.[p] ?? 0;
                const total =
                  (activity?.byPlatform?.ios ?? 0) +
                  (activity?.byPlatform?.android ?? 0) +
                  (activity?.byPlatform?.web ?? 0);
                const pct = total > 0 ? Math.round((count / total) * 100) : 0;
                return (
                  <div
                    key={p}
                    className={cn(
                      'p-4 rounded-xl',
                      isDark ? 'bg-slate-800/40' : 'bg-slate-50',
                    )}
                  >
                    <p
                      className={cn(
                        'text-xs uppercase tracking-wider',
                        isDark ? 'text-slate-400' : 'text-slate-500',
                      )}
                    >
                      {p}
                    </p>
                    <p
                      className={cn(
                        'text-2xl font-bold mt-1',
                        isDark ? 'text-white' : 'text-slate-900',
                      )}
                    >
                      {count}
                    </p>
                    <div
                      className={cn(
                        'mt-3 h-1.5 rounded-full overflow-hidden',
                        isDark ? 'bg-slate-700' : 'bg-slate-200',
                      )}
                    >
                      <div
                        className="h-full bg-[#0EA5A4]"
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                    <p
                      className={cn(
                        'text-xs mt-2',
                        isDark ? 'text-slate-400' : 'text-slate-500',
                      )}
                    >
                      {pct}% of DAU
                    </p>
                  </div>
                );
              })}
            </div>
          </Card>
        </TabsContent>

        {/* Sessions Tab */}
        <TabsContent value="sessions">
          <Card className={cn(
            'rounded-2xl border-0',
            isDark ? 'bg-[#1E293B]' : 'bg-white'
          )}>
            {/* Filters */}
            <div className={cn(
              'p-5 border-b flex flex-col lg:flex-row gap-4',
              isDark ? 'border-slate-700' : 'border-slate-200'
            )}>
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Search by device, IP, user..."
                  value={sessionsSearch}
                  onChange={(e) => {
                    setSessionsSearch(e.target.value);
                    setSessionsPage(1);
                  }}
                  className={cn(
                    'pl-10',
                    isDark && 'bg-slate-800 border-slate-700 text-white'
                  )}
                />
              </div>
              <Select value={sessionsActiveFilter} onValueChange={(v) => { setSessionsActiveFilter(v); setSessionsPage(1); }}>
                <SelectTrigger className={cn('w-[150px]', isDark && 'bg-slate-800 border-slate-700 text-white')}>
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="true">Active</SelectItem>
                  <SelectItem value="false">Inactive</SelectItem>
                </SelectContent>
              </Select>
              <Select value={sessionsPlatformFilter} onValueChange={(v) => { setSessionsPlatformFilter(v); setSessionsPage(1); }}>
                <SelectTrigger className={cn('w-[150px]', isDark && 'bg-slate-800 border-slate-700 text-white')}>
                  <SelectValue placeholder="Platform" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Platforms</SelectItem>
                  <SelectItem value="ios">iOS</SelectItem>
                  <SelectItem value="android">Android</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Table */}
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className={isDark ? 'border-slate-700' : ''}>
                    <TableHead className={isDark ? 'text-slate-400' : ''}>User</TableHead>
                    <TableHead className={isDark ? 'text-slate-400' : ''}>Device</TableHead>
                    <TableHead className={isDark ? 'text-slate-400' : ''}>Location</TableHead>
                    <TableHead className={isDark ? 'text-slate-400' : ''}>Status</TableHead>
                    <TableHead className={isDark ? 'text-slate-400' : ''}>Last Active</TableHead>
                    <TableHead className={isDark ? 'text-slate-400' : ''}>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {isLoading ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8">
                        <RefreshCw className="w-6 h-6 animate-spin mx-auto text-slate-400" />
                      </TableCell>
                    </TableRow>
                  ) : sessions.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className={cn(
                        'text-center py-8',
                        isDark ? 'text-slate-400' : 'text-slate-500'
                      )}>
                        No sessions found
                      </TableCell>
                    </TableRow>
                  ) : sessions.map((session) => (
                    <TableRow key={session.id} className={isDark ? 'border-slate-700' : ''}>
                      <TableCell>
                        <div>
                          <p className={cn('font-medium', isDark ? 'text-white' : 'text-slate-900')}>
                            {session.user?.username || 'Unknown'}
                          </p>
                          <p className={cn('text-sm', isDark ? 'text-slate-400' : 'text-slate-500')}>
                            {session.user?.email || (session.user?.isAnonymous ? 'Anonymous' : '-')}
                          </p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Smartphone className="w-4 h-4 text-slate-400" />
                          <div>
                            <p className={cn('text-sm', isDark ? 'text-slate-300' : 'text-slate-700')}>
                              {session.deviceName || session.deviceModel || 'Unknown Device'}
                            </p>
                            <p className={cn('text-xs', isDark ? 'text-slate-500' : 'text-slate-400')}>
                              {session.platform} {session.osVersion}
                            </p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <MapPin className="w-4 h-4 text-slate-400" />
                          <div>
                            <p className={cn('text-sm', isDark ? 'text-slate-300' : 'text-slate-700')}>
                              {session.country || 'Unknown'}
                            </p>
                            <p className={cn('text-xs', isDark ? 'text-slate-500' : 'text-slate-400')}>
                              {session.ipAddress || '-'}
                            </p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className={session.isActive 
                          ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300'
                          : 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300'
                        }>
                          {session.isActive ? 'Active' : 'Inactive'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Clock className="w-4 h-4 text-slate-400" />
                          <span className={cn('text-sm', isDark ? 'text-slate-300' : 'text-slate-600')}>
                            {formatRelativeTime(session.lastActiveAt)}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setSelectedSession(session)}
                            className={isDark ? 'hover:bg-slate-700' : ''}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          {session.isActive && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setSessionToRevoke(session)}
                              className="text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            {/* Pagination */}
            {sessions.length > 0 && (
              <div className={cn(
                'p-4 border-t',
                isDark ? 'border-slate-700' : 'border-slate-200'
              )}>
                <Pagination
                  isDark={isDark}
                  currentPage={sessionsPage}
                  totalPages={Math.ceil(sessionsTotal / ITEMS_PER_PAGE)}
                  total={sessionsTotal}
                  itemsPerPage={ITEMS_PER_PAGE}
                  onPageChange={setSessionsPage}
                />
              </div>
            )}
          </Card>
        </TabsContent>

        {/* Audit Logs Tab */}
        <TabsContent value="audit-logs">
          <Card className={cn(
            'rounded-2xl border-0',
            isDark ? 'bg-[#1E293B]' : 'bg-white'
          )}>
            {/* Filters */}
            <div className={cn(
              'p-5 border-b flex flex-col lg:flex-row gap-4',
              isDark ? 'border-slate-700' : 'border-slate-200'
            )}>
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Search by IP, device..."
                  value={logsSearch}
                  onChange={(e) => {
                    setLogsSearch(e.target.value);
                    setLogsPage(1);
                  }}
                  className={cn(
                    'pl-10',
                    isDark && 'bg-slate-800 border-slate-700 text-white'
                  )}
                />
              </div>
              <Select value={logsSeverityFilter} onValueChange={(v) => { setLogsSeverityFilter(v); setLogsPage(1); }}>
                <SelectTrigger className={cn('w-[150px]', isDark && 'bg-slate-800 border-slate-700 text-white')}>
                  <SelectValue placeholder="Severity" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Severity</SelectItem>
                  <SelectItem value="INFO">Info</SelectItem>
                  <SelectItem value="WARNING">Warning</SelectItem>
                  <SelectItem value="CRITICAL">Critical</SelectItem>
                </SelectContent>
              </Select>
              <Select value={logsEventTypeFilter} onValueChange={(v) => { setLogsEventTypeFilter(v); setLogsPage(1); }}>
                <SelectTrigger className={cn('w-[180px]', isDark && 'bg-slate-800 border-slate-700 text-white')}>
                  <SelectValue placeholder="Event Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Events</SelectItem>
                  <SelectItem value="LOGIN_SUCCESS">Login Success</SelectItem>
                  <SelectItem value="LOGIN_FAILED">Login Failed</SelectItem>
                  <SelectItem value="SESSION_REVOKED">Session Revoked</SelectItem>
                  <SelectItem value="GEO_ANOMALY">Geo Anomaly</SelectItem>
                  <SelectItem value="ACCOUNT_SHARING_SUSPECTED">Account Sharing</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Table */}
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className={isDark ? 'border-slate-700' : ''}>
                    <TableHead className={isDark ? 'text-slate-400' : ''}>Time</TableHead>
                    <TableHead className={isDark ? 'text-slate-400' : ''}>Event</TableHead>
                    <TableHead className={isDark ? 'text-slate-400' : ''}>Severity</TableHead>
                    <TableHead className={isDark ? 'text-slate-400' : ''}>User</TableHead>
                    <TableHead className={isDark ? 'text-slate-400' : ''}>IP / Location</TableHead>
                    <TableHead className={isDark ? 'text-slate-400' : ''}>Details</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {isLoading ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8">
                        <RefreshCw className="w-6 h-6 animate-spin mx-auto text-slate-400" />
                      </TableCell>
                    </TableRow>
                  ) : auditLogs.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className={cn(
                        'text-center py-8',
                        isDark ? 'text-slate-400' : 'text-slate-500'
                      )}>
                        No audit logs found
                      </TableCell>
                    </TableRow>
                  ) : auditLogs.map((log) => (
                    <TableRow key={log.id} className={isDark ? 'border-slate-700' : ''}>
                      <TableCell>
                        <div>
                          <p className={cn('text-sm', isDark ? 'text-slate-300' : 'text-slate-700')}>
                            {formatRelativeTime(log.createdAt)}
                          </p>
                          <p className={cn('text-xs', isDark ? 'text-slate-500' : 'text-slate-400')}>
                            {formatDate(log.createdAt)}
                          </p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className={eventTypeColors[log.eventType] || 'bg-gray-100 text-gray-800'}>
                          {log.eventType.replace(/_/g, ' ')}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge className={severityColors[log.severity]}>
                          {log.severity}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <p className={cn('text-sm', isDark ? 'text-slate-300' : 'text-slate-700')}>
                          {log.user?.username || log.userId?.slice(0, 8) || '-'}
                        </p>
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className={cn('text-sm', isDark ? 'text-slate-300' : 'text-slate-700')}>
                            {log.ipAddress || '-'}
                          </p>
                          <p className={cn('text-xs', isDark ? 'text-slate-500' : 'text-slate-400')}>
                            {log.country || '-'}
                          </p>
                        </div>
                      </TableCell>
                      <TableCell>
                        {log.details && Object.keys(log.details).length > 0 ? (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              toast.info(
                                <pre className="text-xs max-w-md overflow-auto">
                                  {JSON.stringify(log.details, null, 2)}
                                </pre>
                              );
                            }}
                            className={isDark ? 'hover:bg-slate-700' : ''}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                        ) : (
                          <span className={cn('text-sm', isDark ? 'text-slate-500' : 'text-slate-400')}>-</span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            {/* Pagination */}
            {auditLogs.length > 0 && (
              <div className={cn(
                'p-4 border-t',
                isDark ? 'border-slate-700' : 'border-slate-200'
              )}>
                <Pagination
                  isDark={isDark}
                  currentPage={logsPage}
                  totalPages={Math.ceil(logsTotal / ITEMS_PER_PAGE)}
                  total={logsTotal}
                  itemsPerPage={ITEMS_PER_PAGE}
                  onPageChange={setLogsPage}
                />
              </div>
            )}
          </Card>
        </TabsContent>
      </Tabs>

      {/* Session Details Dialog */}
      <Dialog open={!!selectedSession} onOpenChange={() => setSelectedSession(null)}>
        <DialogContent className={cn(
          'max-w-lg',
          isDark && 'bg-[#1E293B] border-slate-700 text-white'
        )}>
          <DialogHeader>
            <DialogTitle>Session Details</DialogTitle>
            <DialogDescription className={isDark ? 'text-slate-400' : ''}>
              Full information about this session
            </DialogDescription>
          </DialogHeader>
          {selectedSession && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className={cn('text-sm font-medium', isDark ? 'text-slate-400' : 'text-slate-500')}>User</p>
                  <p className={isDark ? 'text-white' : 'text-slate-900'}>
                    {selectedSession.user?.username || 'Unknown'}
                  </p>
                </div>
                <div>
                  <p className={cn('text-sm font-medium', isDark ? 'text-slate-400' : 'text-slate-500')}>Email</p>
                  <p className={isDark ? 'text-white' : 'text-slate-900'}>
                    {selectedSession.user?.email || 'N/A'}
                  </p>
                </div>
                <div>
                  <p className={cn('text-sm font-medium', isDark ? 'text-slate-400' : 'text-slate-500')}>Device</p>
                  <p className={isDark ? 'text-white' : 'text-slate-900'}>
                    {selectedSession.deviceName || selectedSession.deviceModel || 'Unknown'}
                  </p>
                </div>
                <div>
                  <p className={cn('text-sm font-medium', isDark ? 'text-slate-400' : 'text-slate-500')}>Platform</p>
                  <p className={isDark ? 'text-white' : 'text-slate-900'}>
                    {selectedSession.platform} {selectedSession.osVersion}
                  </p>
                </div>
                <div>
                  <p className={cn('text-sm font-medium', isDark ? 'text-slate-400' : 'text-slate-500')}>IP Address</p>
                  <p className={isDark ? 'text-white' : 'text-slate-900'}>
                    {selectedSession.ipAddress || 'N/A'}
                  </p>
                </div>
                <div>
                  <p className={cn('text-sm font-medium', isDark ? 'text-slate-400' : 'text-slate-500')}>Location</p>
                  <p className={isDark ? 'text-white' : 'text-slate-900'}>
                    {selectedSession.city ? `${selectedSession.city}, ` : ''}{selectedSession.country || 'Unknown'}
                  </p>
                </div>
                <div>
                  <p className={cn('text-sm font-medium', isDark ? 'text-slate-400' : 'text-slate-500')}>App Version</p>
                  <p className={isDark ? 'text-white' : 'text-slate-900'}>
                    {selectedSession.appVersion || 'N/A'}
                  </p>
                </div>
                <div>
                  <p className={cn('text-sm font-medium', isDark ? 'text-slate-400' : 'text-slate-500')}>Status</p>
                  <Badge className={selectedSession.isActive 
                    ? 'bg-green-100 text-green-800'
                    : 'bg-gray-100 text-gray-800'
                  }>
                    {selectedSession.isActive ? 'Active' : 'Inactive'}
                  </Badge>
                </div>
                <div>
                  <p className={cn('text-sm font-medium', isDark ? 'text-slate-400' : 'text-slate-500')}>Created</p>
                  <p className={isDark ? 'text-white' : 'text-slate-900'}>
                    {formatDate(selectedSession.createdAt)}
                  </p>
                </div>
                <div>
                  <p className={cn('text-sm font-medium', isDark ? 'text-slate-400' : 'text-slate-500')}>Last Active</p>
                  <p className={isDark ? 'text-white' : 'text-slate-900'}>
                    {formatDate(selectedSession.lastActiveAt)}
                  </p>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Revoke Session Confirmation */}
      <AlertDialog open={!!sessionToRevoke} onOpenChange={() => setSessionToRevoke(null)}>
        <AlertDialogContent className={isDark ? 'bg-[#1E293B] border-slate-700' : ''}>
          <AlertDialogHeader>
            <AlertDialogTitle className={isDark ? 'text-white' : ''}>Revoke Session</AlertDialogTitle>
            <AlertDialogDescription className={isDark ? 'text-slate-400' : ''}>
              Are you sure you want to revoke this session? The user will be logged out from this device.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className={isDark ? 'border-slate-700 text-slate-300 hover:bg-slate-800' : ''}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRevokeSession}
              className="bg-red-600 hover:bg-red-700"
            >
              Revoke
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ─── Helper components ────────────────────────────────────────────────

type ToneKey =
  | 'emerald'
  | 'blue'
  | 'purple'
  | 'indigo'
  | 'slate'
  | 'green'
  | 'cyan'
  | 'amber';

const toneBg: Record<ToneKey, string> = {
  emerald: 'bg-emerald-100 dark:bg-emerald-900/30',
  blue: 'bg-blue-100 dark:bg-blue-900/30',
  purple: 'bg-purple-100 dark:bg-purple-900/30',
  indigo: 'bg-indigo-100 dark:bg-indigo-900/30',
  slate: 'bg-slate-100 dark:bg-slate-800',
  green: 'bg-green-100 dark:bg-green-900/30',
  cyan: 'bg-cyan-100 dark:bg-cyan-900/30',
  amber: 'bg-amber-100 dark:bg-amber-900/30',
};

interface KpiCardProps {
  isDark: boolean;
  icon: React.ReactNode;
  tone: ToneKey;
  label: string;
  value: number;
  hint?: string;
  live?: boolean;
}

function KpiCard({ isDark, icon, tone, label, value, hint, live }: KpiCardProps) {
  return (
    <Card
      className={cn(
        'p-5 rounded-2xl border-0',
        isDark ? 'bg-[#1E293B]' : 'bg-white',
      )}
    >
      <div className="flex items-start gap-4">
        <div className={cn('p-3 rounded-xl', toneBg[tone])}>{icon}</div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <p
              className={cn(
                'text-sm',
                isDark ? 'text-slate-400' : 'text-slate-500',
              )}
            >
              {label}
            </p>
            {live && (
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
              </span>
            )}
          </div>
          <p
            className={cn(
              'text-2xl font-bold mt-0.5',
              isDark ? 'text-white' : 'text-slate-900',
            )}
          >
            {value.toLocaleString()}
          </p>
          {hint && (
            <p
              className={cn(
                'text-xs mt-1',
                isDark ? 'text-slate-500' : 'text-slate-400',
              )}
            >
              {hint}
            </p>
          )}
        </div>
      </div>
    </Card>
  );
}

interface BigStatCardProps {
  isDark: boolean;
  label: string;
  value: number;
  accent: 'blue' | 'purple' | 'indigo';
  icon: React.ReactNode;
}

function BigStatCard({ isDark, label, value, accent, icon }: BigStatCardProps) {
  const gradient = {
    blue: 'from-blue-500/10 to-blue-500/0',
    purple: 'from-purple-500/10 to-purple-500/0',
    indigo: 'from-indigo-500/10 to-indigo-500/0',
  }[accent];
  const text = {
    blue: 'text-blue-600 dark:text-blue-400',
    purple: 'text-purple-600 dark:text-purple-400',
    indigo: 'text-indigo-600 dark:text-indigo-400',
  }[accent];
  return (
    <Card
      className={cn(
        'p-6 rounded-2xl border-0 relative overflow-hidden',
        isDark ? 'bg-[#1E293B]' : 'bg-white',
      )}
    >
      <div
        className={cn(
          'absolute inset-0 bg-gradient-to-br pointer-events-none',
          gradient,
        )}
      />
      <div className="relative">
        <div className="flex items-center justify-between mb-3">
          <p
            className={cn(
              'text-sm font-medium uppercase tracking-wider',
              isDark ? 'text-slate-400' : 'text-slate-500',
            )}
          >
            {label}
          </p>
          <div className={text}>{icon}</div>
        </div>
        <p
          className={cn(
            'text-4xl font-bold',
            isDark ? 'text-white' : 'text-slate-900',
          )}
        >
          {value.toLocaleString()}
        </p>
        <p
          className={cn(
            'text-xs mt-2',
            isDark ? 'text-slate-500' : 'text-slate-400',
          )}
        >
          unique users
        </p>
      </div>
    </Card>
  );
}
